var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { NumberToBoolean } from '../_internal/conversion/converters/NumberToBoolean';
import { UTCDateTimeStringToDateString } from '../_internal/conversion/converters/UTCDateTimeStringToDate';
import { convert } from '../_internal/conversion/module';
import { validateStringArgumentNotEmpty } from '../_internal/validation';
import * as api from '../api/module';
import { paramsToString, parseRequestOptions } from '../api/util';
import { refreshSession } from '../session/module';
import { clearCurrentSiteId, getCurrentSiteId } from './cache';
const siteConversionConfig = {
    created: UTCDateTimeStringToDateString,
    last_modified: UTCDateTimeStringToDateString,
    'metadata.auto_captions': NumberToBoolean,
};
/**
 * Get a list of sites
 */
export function listSites(params, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(Object.assign({ siteId: null }, requestOptions));
        const paramsStr = paramsToString(params);
        const res = yield api.get(`/v2/sites/${paramsStr}`, requestOptions);
        return {
            raw: res,
            content: Object.assign(Object.assign({}, res.data), { sites: res.data.sites.map((site) => convert('toClient', site, siteConversionConfig)) }),
        };
    });
}
/**
 * Get site by id
 */
export function getSite(id, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(Object.assign({ siteId: null }, requestOptions));
        validateStringArgumentNotEmpty(id, 'id');
        const res = yield api.get(`/v2/sites/${id}`, requestOptions);
        return {
            raw: res,
            content: convert('toClient', res.data, siteConversionConfig),
        };
    });
}
/**
 * Create a new site
 */
export function createSite(data, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        requestOptions = parseRequestOptions(Object.assign({ siteId: null }, requestOptions));
        // Only `name` is accepted as an argument to create
        const metadata = {
            name: (_a = data.metadata) === null || _a === void 0 ? void 0 : _a.name,
        };
        const res = yield api.post('/v2/sites/', Object.assign(Object.assign({}, requestOptions), { data: {
                metadata: convert('toServer', metadata, siteConversionConfig),
                is_drm_enabled: data.is_drm_enabled,
            } }));
        refreshSession();
        return {
            raw: res,
            content: convert('toClient', res.data, siteConversionConfig),
        };
    });
}
/**
 * Update a site
 */
export function updateSite(id, data, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(Object.assign({ siteId: null }, requestOptions));
        validateStringArgumentNotEmpty(id, 'id');
        // Clone metadata and remove unsupported properties
        const metadata = Object.assign({}, data.metadata);
        metadata.is_deleted = undefined;
        const res = yield api.patch(`/v2/sites/${id}`, Object.assign(Object.assign({}, requestOptions), { data: {
                id: id,
                // Only pass `metadata`
                metadata: convert('toServer', metadata, siteConversionConfig),
            } }));
        // There's a big chance that the sites information from
        // dashboard_scope is still old. But we'll update the session anyways.
        // Hopefully this can be fixed in rottweiler someday.
        refreshSession();
        return {
            raw: res,
            content: convert('toClient', res.data, siteConversionConfig),
        };
    });
}
/**
 * Deletes a site and all the media present on it
 */
export function deleteSite(id, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(Object.assign({ siteId: null }, requestOptions));
        validateStringArgumentNotEmpty(id, 'id');
        const res = yield api.remove(`/v2/sites/${id}`, requestOptions);
        if (getCurrentSiteId() === id) {
            clearCurrentSiteId();
        }
        refreshSession();
        return {
            raw: res,
            content: res.status === 204,
        };
    });
}
/**
 * Retrieves the amount of media (videos) stored in the provided siteIds
 * @param ids A list of siteIds who's media count should be retrieved
 */
export function getSiteMediaCount(ids, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(Object.assign({ siteId: null }, requestOptions));
        const res = yield api.get(`/v2/site_media_counts/?site_ids=${ids.join(',')}`, requestOptions);
        return {
            raw: res,
            content: res.data.sites,
        };
    });
}
/**
 * Retrieve site user count for multiple sites
 * @param ids List of siteIds to retrieve user count for
 */
export function getSiteUserCount(ids, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(Object.assign({ siteId: null }, requestOptions));
        const res = yield api.get(`/v2/site_user_counts/?site_ids=${ids.join(',')}`, requestOptions);
        return {
            raw: res,
            content: res.data.sites,
        };
    });
}
const splitUrl = (url) => {
    // URL does not support `//`, so we'll trick it into
    // parsing by making it https. The actual protocol
    // isn't used anywhere, so we can get away with this
    if (url.startsWith('//')) {
        url = `https:${url}`;
    }
    const parsedUrl = new URL(url);
    const cutPoint = url.indexOf(parsedUrl.pathname);
    const base = url.slice(0, cutPoint);
    const path = url.slice(cutPoint);
    return [base, path];
};
export function signUrls(payloads, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(requestOptions);
        const res = yield api.put(`/v2/sites/${requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.siteId}/sign_urls`, Object.assign({ data: {
                base_url: 'https://example.com',
                payloads: payloads.map((payload) => {
                    const url = typeof payload === 'object' ? payload.resource : payload;
                    const path = url ? splitUrl(url)[1] : '';
                    const payloadObj = typeof payload === 'object' ? payload : undefined;
                    return Object.assign(Object.assign({}, payloadObj), { resource: path });
                }),
            } }, requestOptions));
        const content = payloads.map((payload, idx) => {
            if (typeof payload === 'undefined') {
                return undefined;
            }
            const url = typeof payload === 'string' ? payload : payload.resource;
            const signedUrl = res.data.urls[idx];
            if (!signedUrl) {
                throw new Error('Missing signedUrl');
            }
            const [originalBase] = splitUrl(url);
            const [, signedPath] = splitUrl(signedUrl);
            if (originalBase && signedPath) {
                return originalBase + signedPath;
            }
        });
        return {
            raw: res,
            // Input is identical to output: (string|undefined)[]
            content: content,
        };
    });
}
export function listLicenses(requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(requestOptions);
        const res = yield api.get(`/v2/sites/${requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.siteId}/licenses`);
        return {
            raw: res,
            content: res.data,
        };
    });
}
